import { useEffect, useState } from "react";
import { srcSetFor } from "@/lib/responsiveImage";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Building2, Compass, LineChart, Landmark, Briefcase, Users2, Home as HomeIcon, Factory, TrendingUp, PieChart, Shield, Scale, ChevronRight } from "lucide-react";
import { MaskReveal, FadeUp, StaggerChildren, StaggerItem } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import SeoHead from "@/components/SeoHead";
import { useCaseStudies } from "@/hooks/usePublicData";
import { useSite } from "@/contexts/SiteContext";

const advises = [
  { icon: HomeIcon, title: "Homeowners", art: "serve-homeowners", alt: "A suburban house with a garden" },
  { icon: Users2, title: "First-Time Buyers", art: "serve-first-time", alt: "House keys in front of an apartment block" },
  { icon: TrendingUp, title: "Property Investors", art: "serve-investors", alt: "A rising bar chart beside stacked gold coins" },
  { icon: Briefcase, title: "Business Owners", art: "serve-business", alt: "Glass-fronted commercial office buildings" },
  { icon: Building2, title: "Developers & Corporations", art: "serve-developers", alt: "A tower crane over a building under construction" },
  { icon: Landmark, title: "Family Offices & Institutions", art: "serve-institutions", alt: "A neoclassical institutional building with columns" },
];

const expertise = [
  {
    title: "Residential Property Advisory",
    tagline: "Making residential decisions with greater clarity.",
    body: "Independent advice across HDB, private and landed properties, assessing location, pricing, transactions, rental potential, tenure and future prospects within the client's financial position, investment horizon and long-term objectives.",
    icon: HomeIcon,
  },
  {
    title: "Commercial Property Advisory",
    tagline: "Looking beyond the headline price.",
    body: "Advisory on office, retail and mixed-use properties, assessing location, tenancy, rental demand, income potential and market evidence while evaluating owner-occupier suitability, investment potential, risks and exit considerations.",
    icon: Building2,
  },
  {
    title: "Industrial & Retail Property",
    tagline: "Understanding the fundamentals behind specialised assets.",
    body: "Advisory on B1/B2 industrial, F&B and retail properties, assessing permitted use, accessibility, tenant demand, rental levels and market potential while considering both operational suitability and investment prospects.",
    icon: Factory,
  },
  {
    title: "Investment Strategy",
    tagline: "Building strategy around your objectives.",
    body: "We develop property strategies aligned with capital, risk appetite, investment horizon and long-term goals, considering acquisitions, existing holdings, portfolio balance, income, appreciation, liquidity and concentration risk.",
    icon: TrendingUp,
  },
  {
    title: "Portfolio Restructuring",
    tagline: "Repositioning portfolios for changing needs.",
    body: "We review existing holdings to identify underperforming, over-concentrated or misaligned assets and, where appropriate, consider restructuring, consolidation, refinancing, reallocation or divestment.",
    icon: PieChart,
  },
  {
    title: "Market Opportunity Analysis",
    tagline: "Turning market information into opportunity.",
    body: "We analyse transactions, pricing, rental trends, supply, demand and market cycles to identify emerging opportunities and changing conditions, supporting better decisions on acquisition, holding, repositioning and exit.",
    icon: LineChart,
  },
  {
    title: "Financial Feasibility",
    tagline: "Understanding the numbers before committing.",
    body: "We assess purchase costs, financing, rental income, expenses, taxes, CPF considerations, cash flow and potential exit proceeds, using scenario analysis to understand potential outcomes under different market and financial conditions.",
    icon: Scale,
  },
  {
    title: "Exit & Divestment Planning",
    tagline: "Planning the exit before it becomes urgent.",
    body: "We assess whether properties should be retained, repositioned or sold, considering performance, market conditions, financing, costs and buyer demand, while helping larger portfolios consider asset sequencing and redeployment of proceeds.",
    icon: Compass,
  },
];

const pillars = [
  {
    kicker: "01 — Collective Expertise",
    // Matches the wording change requested for the advisory team page. Leaving
    // this as "Three advisors" would contradict the paragraph higher up the same
    // page, which now reads "a team of experienced property professionals".
    title: "Team of perspectives. One recommendation.",
    body: "Clients benefit from the combined market experience of PIRAC's property advisors, coordinated through a single research methodology.",
  },
  {
    kicker: "02 — Objective Analysis",
    title: "Evidence over narrative.",
    body: "Every recommendation is evaluated using research, feasibility modelling and independent risk assessment — not market anecdote.",
  },
  {
    kicker: "03 — Consistent Standards",
    title: "The same rigour, every engagement.",
    body: "Whether a first upgrade or a portfolio restructure, every engagement follows the same PIRAC advisory methodology.",
  },
];

const frameworkSteps = [
  { letter: "P", label: "Profile", desc: "Your Objectives" },
  { letter: "I", label: "Investigate", desc: "The Market" },
  { letter: "R", label: "Review", desc: "Opportunities & Risks" },
  { letter: "A", label: "Advise", desc: "The Best Strategy" },
  { letter: "C", label: "Create", desc: "Long-Term Wealth" },
];

const whyPoints = [
  "Independent, research-based advice",
  "Collective team experience",
  "Deep Singapore property knowledge",
  "Personalised strategies",
  "Transparent communication",
  "Long-term relationships",
  "Client-focused outcomes",
];

export default function Home() {
  return (
    <>
      <SeoHead pageKey="home" />
      <Hero />
      <TrustBar />
      <Introduction />
      <WhoAdvises />
      <ExpertiseGrid />
      <PillarsSection />
      <FrameworkPreview />
      <Marquee />
      <FeaturedCaseStudies />
      <WhyPirac />
      <ResourceTools />
      <FinalCTA />
    </>
  );
}

// ---------- Hero ----------
function Hero() {
  const { site } = useSite();
  const whatsapp = (site.whatsappNumber || "").replace(/\D/g, "");
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, 180]);
  const y2 = useTransform(scrollY, [0, 800], [0, 60]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0.4]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 100); return () => clearTimeout(t); }, []);

  return (
    <section className="relative min-h-[92vh] lg:min-h-[100vh] overflow-hidden bg-[#060f1e]" data-testid="home-hero">
      {/* Parallax skyline — brighter */}
      <motion.div
        style={{ y: y1, opacity }}
        className="absolute inset-0 z-0"
      >
        <img
          src="/assets/hero-singapore-skyline-1600.webp"
          srcSet="/assets/hero-singapore-skyline-800.webp 800w, /assets/hero-singapore-skyline-1600.webp 1600w"
          sizes="100vw"
          alt="Marina Bay Singapore skyline at night"
          className="w-full h-full object-cover opacity-95"
          loading="eager"
          fetchpriority="high"
          width="1600"
          height="900"
        />
        {/* Softer dark overlay so image reads brighter, gradient anchors text */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to right, rgba(6,15,30,0.96) 0%, rgba(6,15,30,0.60) 30%, rgba(6,15,30,0.10) 52%, rgba(6,15,30,0) 64%)" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to bottom, rgba(6,15,30,0.35) 0%, rgba(6,15,30,0) 26%, rgba(6,15,30,0) 74%, rgba(6,15,30,0.80) 100%)" }}
        />
      </motion.div>

      {/* Ambient gold lights — animated */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 2, delay: 0.2 }}
        className="absolute inset-0 z-[1] pointer-events-none"
      >
        <motion.div
          animate={{ opacity: [0.35, 0.55, 0.35], scale: [1, 1.05, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(204,162,77,0.18), transparent 70%)" }}
        />
        <motion.div
          animate={{ opacity: [0.25, 0.5, 0.25], scale: [1, 1.08, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
          className="absolute top-1/3 -right-40 w-[720px] h-[720px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(204,162,77,0.12), transparent 70%)" }}
        />
      </motion.div>

      {/* Corner gold hairlines animated */}
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: loaded ? 1 : 0 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.16,1,0.3,1] }}
        className="absolute top-24 lg:top-32 left-6 lg:left-10 h-px w-40 lg:w-56 bg-gradient-to-r from-[#cca24d] to-transparent origin-left z-10" />
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: loaded ? 1 : 0 }} transition={{ duration: 1.2, delay: 0.6, ease: [0.16,1,0.3,1] }}
        className="absolute bottom-24 right-6 lg:right-10 h-px w-40 lg:w-56 bg-gradient-to-l from-[#cca24d] to-transparent origin-right z-10" />

      <div className="relative z-20 mx-auto max-w-[1400px] px-6 lg:px-10 pt-28 lg:pt-32 pb-20">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-9">
            {/* Overline */}
            <FadeUp delay={0.1} onMount>
              {/* Transparent mark, not the JPEG lockup: on this near-black hero
                  the supplied JPEG shows its own lighter navy square. The
                  strapline is cropped off because it is repeated as the
                  overline immediately below. */}
              <img
                src="/assets/pirac-mark.webp"
                alt="PIRAC"
                className="h-28 lg:h-36 w-auto mb-7"
                width="440"
                height="348"
              />
            </FadeUp>

            {/* The strapline was set at the same 0.72rem as every other overline
                on the site, which is why it disappeared under a 6vw headline.
                Here it is the company name, so it gets its own scale. */}
            <FadeUp delay={0.2} onMount>
              <div className="flex items-center gap-4 mb-6">
                <motion.span
                  animate={{ rotate: [45, 225, 45] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="w-2.5 h-2.5 bg-[#cca24d] inline-block shrink-0"
                />
                <span className="font-medium uppercase text-[#cca24d] text-lg md:text-xl lg:text-2xl 2xl:text-3xl tracking-[0.14em] leading-snug">
                  Property Investment Research and Advisory Centre
                </span>
              </div>
            </FadeUp>

            {/* Masked reveal headline — bigger to fill space */}
            <MaskReveal
              lines={["Research Insights.", "Trusted Advice.", "Smarter Options.", "Better Decisions."]}
              className="font-serif text-[7vw] md:text-[4.8vw] lg:text-[3.5vw] xl:text-[3.2vw] font-light tracking-[-0.02em] leading-[1.12] text-[#f7f5f0]"
              delay={0.5}
            />

            <FadeUp delay={1.4} className="mt-8 max-w-2xl" onMount>
              <p className="text-[#e8e6df] text-lg md:text-xl font-light leading-relaxed">
                Independent, research-driven property advice for property owners, investors, business owners, developers and institutions navigating Singapore's real estate market.
              </p>
            </FadeUp>

            <FadeUp delay={1.7} className="mt-9 flex flex-wrap items-center gap-4" onMount>
              <Link
                to="/contact"
                data-testid="hero-cta-book"
                className="group relative inline-flex items-center gap-3 px-7 h-14 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Book a Consultation
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                </span>
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-0 transition-transform duration-700 bg-gradient-to-r from-[#e5c98b] via-[#f0d896] to-[#cca24d]" />
              </Link>
              <Link
                to="/framework"
                data-testid="hero-cta-framework"
                className="group inline-flex items-center gap-3 px-7 h-14 border border-[#cca24d]/40 text-[#f7f5f0] text-sm tracking-wide hover:border-[#cca24d] hover:text-[#cca24d] hover:bg-[#cca24d]/5 transition-colors backdrop-blur-sm"
              >
                Explore the PIRAC Framework
                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              {whatsapp && (
                <a
                  href={`https://wa.me/${whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="min-h-11 py-2 inline-flex items-center text-sm text-[#d8d8d8] hover:text-[#cca24d] editorial-link tracking-wide"
                >
                  Speak with PIRAC
                </a>
              )}
            </FadeUp>
          </div>

          <motion.div style={{ y: y2 }} className="hidden lg:block col-span-3">
            <div className="border-l border-[#cca24d]/30 pl-6 space-y-8">
              <div>
                <div className="overline mb-2">Est.</div>
                <div className="font-serif text-4xl xl:text-5xl text-[#f7f5f0]">Singapore</div>
              </div>
              <div>
                <div className="overline mb-2">Framework</div>
                <div className="font-serif text-4xl xl:text-5xl text-[#f7f5f0]">P.I.R.A.C.</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <FadeUp delay={2} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
        <div className="flex flex-col items-center gap-3 text-[#9ca3af]">
          <span className="overline text-[10px]">Scroll</span>
          <motion.span
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-[#cca24d]/50"
          />
        </div>
      </FadeUp>
    </section>
  );
}

// ---------- Trust bar ----------
function TrustBar() {
  const items = [
    { label: "Experience", val: "Decades Combined" },
    { label: "Sectors", val: "Multiple Property" },
    { label: "Approach", val: "Research-Driven" },
  ];
  return (
    <section className="relative border-y border-[#1a2940] bg-[#060f1e] overflow-hidden" data-testid="home-trust-bar">
      {/* subtle ambient light */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(204,162,77,0.06), transparent 70%)" }} />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1a2940]">
          {items.map((it, i) => (
            <FadeUp key={it.label} delay={i * 0.08} className="bg-[#060f1e] px-6 py-8 group hover:bg-[#0c1728] transition-colors duration-500">
              <div className="overline text-[10px] text-[#cca24d]/80 mb-3 group-hover:text-[#cca24d] transition-colors">{it.label}</div>
              <div className="font-serif text-2xl md:text-3xl text-[#f7f5f0] leading-tight">{it.val}</div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Introduction() {
  return (
    <section className="relative py-24 lg:py-40 bg-[#060f1e] overflow-hidden" data-testid="home-intro">
      {/* ambient gold blob */}
      <motion.div
        aria-hidden
        animate={{ opacity: [0.15, 0.3, 0.15], scale: [1, 1.15, 1] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-40 top-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,162,77,0.12), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <FadeUp>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-[#cca24d]" />
                <span className="overline">Chapter 01 — Position</span>
              </div>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                More Than <em className="text-[#cca24d] not-italic font-normal italic">Property Agents.</em><br />
                Strategic Advisors.<br />
                Trusted Partners.
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <FadeUp delay={0.15} className="space-y-6 text-[#d8d8d8] leading-relaxed text-lg">
              <p>
                At Property Investment Research and Advisory Centre, we believe successful property decisions begin with informed analysis.
              </p>
              <p>
                PIRAC is supported by a team of experienced property professionals with decades of expertise in research and advice across Singapore's residential, commercial, industrial, retail and investment property sectors.
              </p>
              <p>
                Our advisors work as one integrated team, combining market intelligence, financial analysis and practical experience to help clients make decisions aligned with their financial goals and long-term aspirations.
              </p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoAdvises() {
  return (
    <section className="relative bg-[#eee8dc] text-[#202733] py-24 lg:py-32 overflow-hidden" data-testid="home-who-advises">
      {/* corner treatment */}
      <div className="absolute top-0 left-0 w-40 h-40 border-t border-l border-[#cca24d]/40 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-40 h-40 border-b border-r border-[#cca24d]/40 pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6"><span className="h-px w-8 bg-[#cca24d]" /><span className="overline text-[#cca24d]">Chapter 02 — Who We Advise</span></div>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight max-w-xl">
              Six client profiles. One methodology.
            </h2>
          </div>
          <p className="max-w-md text-sm text-[#202733]/70 leading-relaxed">
            PIRAC's advisory framework adapts to who you are — but never dilutes. The research standard remains constant across every engagement.
          </p>
        </div>

        <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#202733]/15">
          {advises.map((a, i) => {
            const Icon = a.icon;
            return (
              <StaggerItem key={a.title} className="relative bg-[#eee8dc] p-6 lg:p-7 hover:bg-[#f7f5f0] transition-colors duration-500 group overflow-hidden">
                <span className="pointer-events-none absolute -top-16 -right-16 w-40 h-40 bg-[#cca24d]/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative flex items-start justify-between mb-6">
                  <span className="font-serif text-3xl text-[#cca24d]">0{i + 1}</span>
                  <motion.div
                    whileHover={{ rotate: 12, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="w-10 h-10 flex items-center justify-center border border-[#202733]/20 group-hover:border-[#cca24d] transition-colors"
                  >
                    <Icon className="h-5 w-5 text-[#202733]/70 group-hover:text-[#cca24d] transition-colors" strokeWidth={1.25} />
                  </motion.div>
                </div>
                <motion.img
                  src={"/assets/" + a.art + ".webp"}
                  alt={a.alt}
                  width="360"
                  height="360"
                  loading="lazy"
                  whileHover={{ scale: 1.04 }}
                  transition={{ type: "spring", stiffness: 200 }}
                  className="relative block w-full max-w-[140px] mx-auto mb-5 select-none"
                />
                <h3 className="relative font-serif text-xl md:text-2xl leading-tight group-hover:text-[#cca24d] transition-colors">{a.title}</h3>
                <div className="relative mt-4 h-px w-12 bg-[#cca24d] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </StaggerItem>
            );
          })}
        </StaggerChildren>
      </div>
    </section>
  );
}

function ExpertiseGrid() {
  return (
    <section className="relative bg-[#060f1e] py-24 lg:py-32 border-t border-[#1a2940] overflow-hidden" data-testid="home-expertise">
      {/* ambient right blob */}
      <motion.div aria-hidden animate={{ opacity: [0.15, 0.35, 0.15] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-40 top-1/3 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,162,77,0.12), transparent 70%)" }} />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-14">
          <FadeUp>
            <div className="flex items-center gap-3 mb-6"><span className="h-px w-8 bg-[#cca24d]" /><span className="overline">Chapter 03 — Advisory Expertise</span></div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-4xl leading-[1.05]">
              Eight Disciplines. <em className="text-[#cca24d] not-italic font-normal italic">One Integrated Advisory Approach.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-8 max-w-3xl text-lg text-[#d8d8d8] font-light leading-relaxed">
              At PIRAC, we believe property decisions require more than price and location. We combine
              research, market intelligence, financial analysis and strategy to help clients make informed
              decisions aligned with their objectives.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.2}>
          <a
            href="/assets/home-eight-disciplines.jpeg"
            target="_blank"
            rel="noopener noreferrer"
            className="block mb-14 border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
            aria-label="The eight PIRAC advisory disciplines, shown as one integrated wheel — open full size"
          >
            <img
              src="/assets/home-eight-disciplines-1600.webp"
                srcSet="/assets/home-eight-disciplines-800.webp 800w, /assets/home-eight-disciplines-1600.webp 1600w"
                sizes="(min-width: 1024px) 94vw, 92vw"
              alt="PIRAC's eight advisory disciplines arranged around a central wheel: residential, commercial and industrial property advisory, investment strategy, portfolio restructuring, market opportunity analysis, financial feasibility, and exit and divestment planning"
              className="w-full h-auto block"
              width="1600"
              height="1060"
              loading="lazy"
            />
          </a>
          <a
            href="/assets/home-eight-disciplines.jpeg"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
          >
            <span className="h-px w-6 bg-[#cca24d]" />
            Open the full-size diagram
          </a>
        </FadeUp>

        <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a2940]">
          {expertise.map((e, i) => {
            const Icon = e.icon;
            return (
              <StaggerItem key={e.title} className="relative bg-[#0c1728] p-8 hover:bg-[#0f1c33] transition-colors duration-500 group overflow-hidden">
                {/* corner shimmer */}
                <span className="pointer-events-none absolute -top-16 -right-16 w-32 h-32 bg-[#cca24d]/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative flex items-start justify-between mb-8">
                  <div className="text-xs text-[#cca24d]/60 tnum">0{i + 1}</div>
                  <motion.div
                    whileHover={{ rotate: 15, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className="w-10 h-10 flex items-center justify-center border border-[#cca24d]/30 group-hover:border-[#cca24d] transition-colors"
                  >
                    <Icon className="h-4 w-4 text-[#cca24d]" strokeWidth={1.25} />
                  </motion.div>
                </div>
                <h3 className="relative font-serif text-2xl text-[#f7f5f0] leading-tight mb-2 group-hover:text-[#cca24d] transition-colors">{e.title}</h3>
                <p className="relative text-sm text-[#cca24d]/80 italic mb-4">{e.tagline}</p>
                <p className="relative text-sm text-[#9ca3af] leading-relaxed">{e.body}</p>
              </StaggerItem>
            );
          })}
        </StaggerChildren>

        {/* Closing statement — the disciplines are meant to be read as one system. */}
        <FadeUp delay={0.1}>
          <div className="mt-px bg-[#0c1728] border-t border-[#cca24d]/25 p-8 lg:p-14">
            <h3 className="font-serif text-3xl md:text-4xl text-[#f7f5f0] font-light tracking-tight">
              One Property. <em className="text-[#cca24d] not-italic font-normal italic">A Wider Perspective.</em>
            </h3>
            <div className="mt-6 space-y-5 max-w-4xl text-[#d8d8d8] leading-relaxed">
              <p>
                At PIRAC, our eight advisory disciplines are designed to work together rather than operate
                as isolated services.
              </p>
              <p>
                A residential acquisition may require market opportunity analysis and financial feasibility.
                A commercial investment may require investment strategy and exit planning. A portfolio review
                may lead to restructuring, divestment and subsequent reinvestment.
              </p>
              <p>
                By bringing these disciplines together, PIRAC provides clients with a more complete
                perspective — helping them move from property transactions to property decisions, and from
                individual opportunities to long-term strategy.
              </p>
            </div>
            <div className="mt-10 pt-8 border-t border-[#1a2940]">
              <div className="font-serif text-xl text-[#f7f5f0]">
                PIRAC <span className="text-[#cca24d]">—</span> Property Investment Research Advisory Centre
              </div>
              <div className="mt-2 overline text-[#cca24d]/80">
                Research. Insight. Strategy. Informed Decisions.
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function PillarsSection() {
  return (
    <section className="relative bg-[#060f1e] py-24 lg:py-40 overflow-hidden" data-testid="home-pillars">
      {/* ambient blob */}
      <motion.div aria-hidden animate={{ opacity: [0.2, 0.4, 0.2], scale: [1, 1.1, 1] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-1/2 -top-40 w-[900px] h-[900px] rounded-full -translate-x-1/2 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,162,77,0.08), transparent 65%)" }} />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-16 max-w-3xl">
          <FadeUp><div className="flex items-center gap-3 mb-6"><span className="h-px w-8 bg-[#cca24d]" /><span className="overline">Chapter 04 — Advisory Model</span></div></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl text-[#f7f5f0] font-light tracking-tight leading-[1.02]">
              One Advisory Team.<br />
              <em className="text-[#cca24d] not-italic italic font-normal">One Research-Led Approach.</em>
            </h2>
          </FadeUp>
        </div>
        <StaggerChildren className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-4">
          {pillars.map((p, i) => (
            <StaggerItem key={p.title} className="relative bg-[#0c1728] p-10 lg:p-12 border border-[#1a2940] group hover:border-[#cca24d]/60 transition-colors duration-700 overflow-hidden">
              {/* animated corner light */}
              <motion.span
                aria-hidden
                animate={{ opacity: [0, 0.6, 0], x: [-100, 100, 300] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: i * 2 }}
                className="absolute top-0 left-0 h-px w-32 bg-gradient-to-r from-transparent via-[#cca24d] to-transparent"
              />
              <div className="overline text-[#cca24d] mb-8">{p.kicker}</div>
              <h3 className="font-serif text-3xl md:text-4xl text-[#f7f5f0] leading-tight mb-6">{p.title}</h3>
              <p className="text-[#9ca3af] leading-relaxed">{p.body}</p>
              <div className="absolute inset-x-10 lg:inset-x-12 bottom-10 lg:bottom-12 h-px bg-[#1a2940] group-hover:bg-[#cca24d]/50 transition-colors" />
            </StaggerItem>
          ))}
        </StaggerChildren>
      </div>
    </section>
  );
}

function FrameworkPreview() {
  return (
    <section className="bg-[#0c1728] py-24 lg:py-40 border-y border-[#1a2940] relative overflow-hidden" data-testid="home-framework-preview">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8 mb-16 items-end">
          <div className="col-span-12 lg:col-span-8">
            <FadeUp><span className="overline">Chapter 05 — The Framework</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl text-[#f7f5f0] font-light tracking-tight leading-[1.02]">
                The P.I.R.A.C.<br />
                <em className="text-[#cca24d] not-italic italic font-normal">Property Advisory Framework.</em>
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:text-right">
            <FadeUp delay={0.2}>
              <p className="text-sm text-[#9ca3af] max-w-xs lg:ml-auto">Research. Strategy. Confidence. Every property decision, backed by evidence.</p>
            </FadeUp>
          </div>
        </div>

        {/* Timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.5, ease: [0.16,1,0.3,1] }} className="absolute inset-x-0 top-12 h-px bg-gradient-to-r from-transparent via-[#cca24d]/60 to-transparent origin-left" />
            <div className="grid grid-cols-5 gap-4 relative">
              {frameworkSteps.map((s, i) => (
                <FadeUp key={s.letter} delay={i * 0.1}>
                  <div className="group cursor-default">
                    <div className="flex items-center justify-center h-24">
                      <motion.div
                        whileHover={{ scale: 1.12, y: -4 }}
                        transition={{ type: "spring", stiffness: 200, damping: 12 }}
                        className="relative w-20 h-20 flex items-center justify-center bg-[#0c1728] border border-[#cca24d]/40 group-hover:border-[#cca24d] transition-colors"
                      >
                        <span className="font-serif text-4xl text-[#cca24d]">{s.letter}</span>
                        <motion.span
                          className="absolute inset-0 border border-[#cca24d] opacity-0 group-hover:opacity-100"
                          animate={{ scale: [1, 1.15, 1], opacity: [0, 0.6, 0] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                        />
                      </motion.div>
                    </div>
                    <div className="mt-6 text-center">
                      <div className="font-serif text-2xl text-[#f7f5f0] group-hover:text-[#cca24d] transition-colors">{s.label}</div>
                      <div className="text-xs text-[#9ca3af] mt-1 tracking-wider uppercase">{s.desc}</div>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden space-y-6">
          {frameworkSteps.map((s) => (
            <FadeUp key={s.letter}>
              <div className="flex items-center gap-6 p-6 border border-[#1a2940] bg-[#060f1e]">
                <div className="w-14 h-14 flex items-center justify-center border border-[#cca24d]/40 flex-shrink-0">
                  <span className="font-serif text-2xl text-[#cca24d]">{s.letter}</span>
                </div>
                <div>
                  <div className="font-serif text-xl text-[#f7f5f0]">{s.label}</div>
                  <div className="text-xs text-[#9ca3af] tracking-wider uppercase">{s.desc}</div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.5} className="mt-14 text-center">
          <Link to="/framework" className="inline-flex items-center gap-3 text-sm text-[#cca24d] tracking-wide editorial-link">
            Read the full framework <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

function FeaturedCaseStudies() {
  // Featured engagements first; if none are flagged, fall back to the three most
  // recent so the section is never empty on a freshly seeded site.
  const featured = useCaseStudies({ pageSize: 3, featured: "true" });
  const recent = useCaseStudies({ pageSize: 3 });

  const items =
    featured.data && featured.data.items.length > 0
      ? featured.data.items
      : (recent.data && recent.data.items) || [];

  // Rendering the heading above an empty grid looks broken; omit the whole
  // section until there is something to show.
  if (!items.length) return null;

  return (
    <section className="bg-[#060f1e] py-24 lg:py-32" data-testid="home-case-studies">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <FadeUp><span className="overline">Chapter 06 — Selected Engagements</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-3xl">
                Featured Case Studies.
              </h2>
            </FadeUp>
          </div>
          <Link to="/case-studies" className="text-sm text-[#cca24d] tracking-wide editorial-link inline-flex items-center gap-2">
            All case studies <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Link>
        </div>

        <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((cs) => (
            <StaggerItem key={cs.id}>
              <Link to={cs.path} className="group block">
                <div className="relative overflow-hidden aspect-[4/5] bg-[#0c1728]">
                  {cs.featuredImage && (
                    <img
                      src={cs.featuredImage}
                      srcSet={srcSetFor(cs.featuredImage)}
                      sizes="(min-width: 1024px) 30vw, (min-width: 768px) 30vw, 90vw"
                      alt={cs.featuredImageAlt || cs.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-1000"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060f1e] via-[#060f1e]/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                    {cs.category && <div className="text-xs text-[#cca24d] tracking-widest uppercase mb-3">{cs.category}</div>}
                    <h3 className="font-serif text-2xl lg:text-3xl text-[#f7f5f0] leading-tight">{cs.title}</h3>
                    <div className="mt-6 flex items-center gap-2 text-sm text-[#d8d8d8] group-hover:text-[#cca24d] transition-colors">
                      Read case study <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerChildren>
      </div>
    </section>
  );
}

function WhyPirac() {
  return (
    <section className="bg-[#060f1e] py-24 lg:py-32 border-t border-[#1a2940]" data-testid="home-why">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <FadeUp><span className="overline">Chapter 07 — Why PIRAC</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[1.05]">
                Considered. Independent.<br />
                <em className="text-[#cca24d] not-italic italic font-normal">Long-view.</em>
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <StaggerChildren className="divide-y divide-[#1a2940] border-t border-[#1a2940]">
              {whyPoints.map((p, i) => (
                <StaggerItem key={p}>
                  <div className="flex items-center justify-between py-5 group">
                    <div className="flex items-baseline gap-6">
                      <span className="text-xs text-[#cca24d]/60 tnum">0{i + 1}</span>
                      <span className="font-serif text-xl md:text-2xl text-[#f7f5f0] group-hover:text-[#cca24d] transition-colors">{p}</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#1a2940] group-hover:text-[#cca24d] transition-colors" strokeWidth={1.5} />
                  </div>
                </StaggerItem>
              ))}
            </StaggerChildren>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResourceTools() {
  const tools = [
    { title: "Property Valuation", body: "An instant, evidence-based estimate from recent comparable transactions.", to: "/property-valuation", icon: Scale, art: "tool-valuation", alt: "A house beside a clipboard of charts and a magnifying glass" },
    { title: "Singapore Property Calculators", body: "Mortgage, purchase and sale proceeds — designed for Singapore rules.", to: "/calculators", icon: PieChart, art: "tool-calculators", alt: "A calculator, a property document and stacked coins" },
    { title: "Property Insights", body: "Long-form research from PIRAC's advisory team.", to: "/blogs", icon: LineChart, art: "tool-insights", alt: "A laptop showing research charts under a magnifying glass" },
  ];
  return (
    <section className="bg-[#eee8dc] text-[#202733] py-24 lg:py-32" data-testid="home-resources">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-16 max-w-3xl">
          <FadeUp><span className="overline text-[#cca24d]">Chapter 08 — Resources</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">Tools & Intelligence.</h2>
          </FadeUp>
        </div>
        <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#202733]/15">
          {tools.map((t, i) => {
            const Icon = t.icon;
            const Inner = (
              <div className="p-10 bg-[#eee8dc] h-full group hover:bg-[#f7f5f0] transition-colors duration-500">
                <div className="flex justify-between items-start mb-6">
                  <span className="font-serif text-3xl text-[#cca24d]">0{i + 1}</span>
                  <Icon className="h-6 w-6 text-[#202733]/60 group-hover:text-[#cca24d] transition-colors" strokeWidth={1} />
                </div>
                <img
                  src={"/assets/" + t.art + ".webp"}
                  alt={t.alt}
                  width="340"
                  height="340"
                  loading="lazy"
                  className="block w-full max-w-[220px] mx-auto mb-8 select-none transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-4">{t.title}</h3>
                <p className="text-sm text-[#202733]/70 leading-relaxed mb-8">{t.body}</p>
                <div className="inline-flex items-center gap-2 text-sm text-[#202733] group-hover:text-[#cca24d] transition-colors">
                  Open <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                </div>
              </div>
            );
            return (
              <StaggerItem key={t.title}>
                {t.external ? (
                  <a href={t.href} target="_blank" rel="noopener noreferrer" data-testid={`resource-${i}`}>{Inner}</a>
                ) : (
                  <Link to={t.to} data-testid={`resource-${i}`}>{Inner}</Link>
                )}
              </StaggerItem>
            );
          })}
        </StaggerChildren>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { site } = useSite();
  const whatsapp = (site.whatsappNumber || "").replace(/\D/g, "");

  return (
    <section className="relative bg-[#060f1e] py-24 lg:py-40 overflow-hidden" data-testid="home-final-cta">
      {/* twin ambient blobs */}
      <motion.div aria-hidden animate={{ opacity: [0.2, 0.5, 0.2], scale: [1, 1.15, 1] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -left-40 top-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,162,77,0.18), transparent 65%)" }} />
      <motion.div aria-hidden animate={{ opacity: [0.15, 0.4, 0.15], scale: [1, 1.1, 1] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute -right-40 bottom-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(204,162,77,0.12), transparent 65%)" }} />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 text-center relative z-10">
        <FadeUp>
          <div className="inline-flex items-center gap-3"><span className="h-px w-8 bg-[#cca24d]" /><span className="overline">Engage</span><span className="h-px w-8 bg-[#cca24d]" /></div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl text-[#f7f5f0] font-light tracking-tight leading-[0.98]">
            Let's Build Your<br />
            <em className="text-[#cca24d] not-italic italic font-normal">Property Future</em> Together.
          </h2>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-14 flex flex-wrap items-center justify-center gap-4">
          <Link to="/contact" data-testid="final-cta-book" className="group relative inline-flex items-center gap-3 px-8 h-14 bg-[#cca24d] text-[#060f1e] text-sm font-medium tracking-wide hover:bg-[#e5c98b] transition-colors overflow-hidden">
            <span className="relative z-10 flex items-center gap-3">Book a Consultation<ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} /></span>
          </Link>
          <a href={whatsapp ? `https://wa.me/${whatsapp}` : "/contact"} target={whatsapp ? "_blank" : undefined} rel="noopener noreferrer" data-testid="final-cta-whatsapp" className="inline-flex items-center gap-3 px-8 h-14 border border-[#cca24d]/40 text-[#f7f5f0] text-sm tracking-wide hover:border-[#cca24d] hover:text-[#cca24d] hover:bg-[#cca24d]/5 transition-colors">
            WhatsApp PIRAC
          </a>
        </FadeUp>
      </div>
      {/* editorial mark */}
      <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 watermark-num select-none pointer-events-none">PIRAC</div>
    </section>
  );
}
